import { useCallback, useEffect, useState } from "react";

import { CreditCard, Plus } from "lucide-react";
import { Switch } from "../../components/ui/switch.jsx";
import { Label } from "../../components/ui/label.jsx";

import PageHeader from "../../components/shared/PageHeader";
import Pagination from "../../components/shared/Pagination";
import SearchInput from "../../components/shared/SearchInput";
import { useApi } from "../../context/ApiContext";
import { useAuth } from "../../context/AuthContext";
import { useData } from "../../context/DataContext";
import { usePagination } from "../../hooks/usePagination";
import DocumentViewer from "../Photos/component/DocumentViewer";

import PaymentDialog from "./component/PaymentDialog";
import PaymentsTab from "./component/PaymentsTab";

const Payments = () => {
  const { user } = useAuth();
  const api = useApi();
  const { globalProjectId } = useData();
  const [allVendors, setAllVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paymentSearch, setPaymentSearch] = useState("");
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [showDeleted, setShowDeleted] = useState(false);

  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [viewerData, setViewerData] = useState("");

  // Filters
  const [vendorFilter, setVendorFilter] = useState("all");
  const [contractorFilter, setContractorFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [invoiceFilter, setInvoiceFilter] = useState("all");
  const [sortBy, setSortBy] = useState("createdAt");
  const [direction, setDirection] = useState("desc");

  const paymentPagination = usePagination(10);
  const {
    items: payments,
    currentPage: paymentPage,
    pageSize: paymentPageSize,
    totalItems: paymentTotalItems,
    setPaginationData: setPaymentPaginationData,
    setCurrentPage: setPaymentPage
  } = paymentPagination;

  const fetchAllVendors = useCallback(async () => {
    try {
      const response = await api.get(`/vendors?page=0&size=1000`);
      if (response.status === 200) {
        const vendorList = response.data.vendors || response.data.items || response.data.content || [];
        setAllVendors(vendorList);
      }
    } catch (error) {
      console.error("Error fetching all vendors:", error);
    }
  }, [api]);

  const fetchPayments = useCallback(
    async (page) => {
      try {
        setLoading(true);
        const params = {
          page,
          size: paymentPageSize,
          search: paymentSearch.trim() || undefined,
          sortBy: sortBy || undefined,
          direction: direction || undefined,
          showDeleted: showDeleted
        };

        if (vendorFilter !== "all") params.vendorId = vendorFilter;
        if (contractorFilter !== "all") params.contractorId = contractorFilter;
        if (globalProjectId !== "ALL" && globalProjectId) params.projectId = globalProjectId;
        if (statusFilter !== "all") params.status = statusFilter;
        if (invoiceFilter !== "all") params.invoiceId = invoiceFilter;

        const response = await api.get(`/payments`, { params });
        setPaymentPaginationData(response.data);
      } catch (error) {
        console.error("Error fetching payments:", error);
      } finally {
        setLoading(false);
      }
    },
    [
      api,
      paymentPageSize,
      paymentSearch,
      setPaymentPaginationData,
      vendorFilter,
      contractorFilter,
      globalProjectId,
      statusFilter,
      invoiceFilter,
      sortBy,
      direction,
      showDeleted
    ]
  );

  const handleFilterChange = (type, value) => {
    setPaymentPage(0);
    if (type === "vendor") setVendorFilter(value);
    if (type === "contractor") setContractorFilter(value);
    if (type === "status") setStatusFilter(value);
    if (type === "invoice") setInvoiceFilter(value);
    if (type === "sortBy") setSortBy(value);
    if (type === "direction") setDirection(value);
  };

  useEffect(() => {
    fetchAllVendors();
  }, [fetchAllVendors]);

  useEffect(() => {
    setPaymentPage(0);
  }, [globalProjectId, setPaymentPage]);

  useEffect(() => {
    fetchPayments(paymentPage);
  }, [paymentPage, paymentPageSize, fetchPayments]);

  const handleAddPayment = () => {
    setEditingPayment(null);
    setIsPaymentDialogOpen(true);
  };

  const handleEditPayment = (payment) => {
    setEditingPayment(payment);
    setIsPaymentDialogOpen(true);
  };

  const openViewer = (data) => {
    setViewerData(data);
    setIsViewerOpen(true);
  };

  if (!user?.permissions?.payment?.read && user?.role !== "super_admin") {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center p-8 bg-yale-blue-50 dark:bg-yale-blue-900/50 rounded-xl border border-dashed border-slate-200 dark:border-yale-blue-800">
        <div className="bg-slate-200 dark:bg-yale-blue-800 p-4 rounded-full mb-4">
          <CreditCard className="w-8 h-8 text-slate-400 dark:text-yale-blue-500" />
        </div>
        <h3 className="text-xl font-semibold text-yale-blue-900 dark:text-yale-blue-50 mb-2">Access Restricted</h3>
        <p className="text-yale-blue-500 dark:text-slate-400 max-w-xs mx-auto">
          You don't have permission to view payments. Please contact your system administrator for access.
        </p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col animate-in fade-in duration-500">
      <div className="flex-1 flex flex-col pb-8">
        <PageHeader
          title="Payment Records"
          subtitle="Track and manage vendor and contractor payments"
          pageIcon={CreditCard}
          actionLabel={user?.permissions?.payment?.create ? "Add Payment" : null}
          onActionClick={handleAddPayment}
          icon={Plus}
          extraActions={
            <div className="flex items-center gap-4">
              <SearchInput
                placeholder="Search payments..."
                className="w-full md:w-64"
                onSearch={(val) => {
                  setPaymentSearch(val);
                  setPaymentPage(0);
                }}
                value={paymentSearch}
              />
            </div>
          }
        />

        <div className="flex-1 flex flex-col">
          <PaymentsTab
            payments={payments}
            vendors={allVendors}
            fetchVendorData={() => fetchPayments(paymentPage)}
            onEditPayment={handleEditPayment}
            onViewDocument={openViewer}
            filters={{
              vendorId: vendorFilter,
              contractorId: contractorFilter,
              status: statusFilter,
              invoiceId: invoiceFilter,
              sortBy,
              direction
            }}
            onFilterChange={handleFilterChange}
            showDeleted={showDeleted}
            setShowDeleted={setShowDeleted}
            setPaymentPage={setPaymentPage}
          />
        </div>

        <div className="mt-auto pt-6">
          <Pagination pagination={paymentPagination} />
        </div>
      </div>

      <PaymentDialog
        isOpen={isPaymentDialogOpen}
        onOpenChange={setIsPaymentDialogOpen}
        onSuccess={() => fetchPayments(paymentPage)}
        editingPayment={editingPayment}
        vendors={allVendors}
        selectedProject={globalProjectId}
      />

      <DocumentViewer isOpen={isViewerOpen} onClose={() => setIsViewerOpen(false)} data={viewerData} />
    </div>
  );
};

export default Payments;
